import React, { useCallback, useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { PaginatedFamilyTheme } from '../helpers/usePaginatedFamilyThemes';
import styles from './PaginatedThemeCard.scss';
import { Body1, Caption } from '@xo-union/tk-ui-typography';
import ThemeListImageContainer from '../Themes/ThemeListImageContainer';
import TrackingContext from '../helpers/TrackingContext';
import { useWebsiteLayoutContext } from '../../context/WebsiteLayoutContext';

interface TrackingProps {
  createGridOrder?: string;
  page?: string;
  partnerName?: string | null;
  colorwayTheme?: boolean;
  colorwayPosition?: number | null;
  viewedByColorwayClick?: boolean;
}

interface PaginatedThemeCardProps {
  family: PaginatedFamilyTheme;
  index: number;
  themesCount?: number;
  trackingProps?: TrackingProps;
}

export const PaginatedThemeCard: React.FC<PaginatedThemeCardProps> = ({
  family,
  index,
  themesCount,
  trackingProps = { createGridOrder: 'random infinite scroll' },
}) => {
  const { track, checkGridImpression, addGridImpression } = useContext(TrackingContext);
  const theme = family.themes?.[0] ?? null;
  const themeId = theme?.id ?? null;
  const themeName = theme?.name ?? null;
  const themeFamily = theme?.family ?? null;
  const familyName = themeFamily ?? themeName ?? family.family ?? null;
  const [inViewRef, inView] = useInView({ threshold: 1, triggerOnce: true });
  const { layout } = useWebsiteLayoutContext();

  const themesCountRef = React.useRef<number | undefined>(themesCount);

  useEffect(() => {
    themesCountRef.current = themesCount;
  }, [themesCount]);

  const trackImpression = useCallback(() => {
    if (themeId != null && !checkGridImpression(themeId)) {
      track('Design Viewed', {
        themeId,
        themePosition: index + 1,
        designName: themeName,
        familyName,
        partnerName: null,
        colorwayTheme: false,
        colorwayPosition: null,
        page: 'paginated theme grid',
        viewedByColorwayClick: false,
        themeCount: themesCountRef.current ?? null,
        ...trackingProps,
      });
      addGridImpression(themeId);
    }
  }, [addGridImpression, checkGridImpression, trackingProps, familyName, index, themeId, themeName, track]);
  useEffect(() => {
    if (inView) trackImpression();
  }, [inView, trackImpression]);

  const trackDesignSelection = () => {
    track('Design Selection', {
      createGridOrder: trackingProps.createGridOrder,
      page: 'wws create theme grid',
      selection: 'select',
      layout,
      themeId,
      themePosition: index + 1,
      designName: themeName,
      familyName,
      partnerName: null,
      themeCount: themesCount ?? null,
    });
  };

  return (
    <div ref={inViewRef} data-testid={`paginated-theme-card-${themeId ?? index}`}>
      <div className={styles.card}>
        {themeId != null ? (
          <ThemeListImageContainer
            mediaId={theme?.previewImage?.mediaApiId}
            mediaUrl={theme?.previewImage?.mediaUrl}
            mobileMediaId={theme?.mobilePreviewImage?.mediaApiId}
            mobileMediaUrl={theme?.mobilePreviewImage?.mediaUrl}
            themeId={themeId}
            onClick={trackDesignSelection}
          />
        ) : (
          <div className={styles.placeholder}>No preview available</div>
        )}
      </div>
      <div className={styles.meta}>
        <>
          <Body1 className={styles.themeTitle}>{theme?.name}</Body1>
          {theme?.designerUrl && (
            <Caption href={theme?.designerUrl} as="a" target="_blank" rel="noopener noreferrer">
              {`See matching invitations`}
            </Caption>
          )}
        </>
      </div>
    </div>
  );
};
